/**
 * Uses Radix DropdownMenu, but because we want to toggle the dropdown menu
 * on hover, we have to rewrite _most_ of the event listeners that
 * would otherwise be out of the box with Radix
 */

import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { ClosedSavedItems, SAVED_ITEMS_MENU_ID } from '@/components/modules/app-navigation/closed/saved-items';
import { useSavedItems } from '@/hooks/use-saved-items';
import { APP_NAV_ITEM_HOME, APP_NAV_ITEM_NOTIFICATIONS, AppNavItem } from '@/lib/constants';
import { userNotificationsState } from '@/state/user-notifications';
import { tv } from '@/utils/styles';

import { NavButton } from '../nav-button';
import { ClosedItemDropdown } from './item-dropdown';

interface Props {
  isOpen: boolean | undefined;
  navItems: AppNavItem[];
  handleOpenNotifications: () => void;
  handleNavigateTo: (url?: string) => void;
  isUserNotificationsEnabled?: boolean;
}

const AppNavigationClosed = ({
  navItems,
  isOpen,
  handleOpenNotifications,
  handleNavigateTo,
  isUserNotificationsEnabled = false,
}: Props) => {
  const { savedItems, deleteSavedItem } = useSavedItems();
  const [openDropdownMenuId, setOpenDropdownMenuId] = useState<string | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const { unreadCount } = useRecoilValue(userNotificationsState);

  const debouncedDropdownMenuPointerLeave = debounce(() => {
    if (!isTouchDevice) {
      setOpenDropdownMenuId(null);
    }
  }, 250);

  const handleDropdownMenuPointerEnter = (
    event: React.PointerEvent<HTMLButtonElement | HTMLDivElement>,
    id: string,
  ) => {
    if (!isTouchDevice && (event.pointerType === 'mouse' || event.pointerType === 'touch')) {
      debouncedDropdownMenuPointerLeave.cancel();
      setOpenDropdownMenuId(id);
    }
  };

  const handleDropdownMenuPointerLeave = (event: React.PointerEvent<HTMLButtonElement | HTMLDivElement>) => {
    if (!isTouchDevice && (event.pointerType === 'mouse' || event.pointerType === 'touch')) {
      debouncedDropdownMenuPointerLeave();
    }
  };

  const handleDropdownMenuKeyDown = (event: React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>, id: string) => {
    if (!isTouchDevice) {
      if (['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        setOpenDropdownMenuId(id);
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpenDropdownMenuId(null);
      }
    }
  };

  useEffect(() => {
    // Set isTouchDevice based on the ability to detect touch event handlers
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const { base, divider } = styles({
    isOpen,
  });

  return (
    <nav className={base()}>
      <NavButton onClick={() => handleNavigateTo(APP_NAV_ITEM_HOME.url)} item={APP_NAV_ITEM_HOME} isOpen={false} />
      {isUserNotificationsEnabled ? (
        <NavButton
          onClick={handleOpenNotifications}
          item={APP_NAV_ITEM_NOTIFICATIONS}
          isOpen={false}
          badgeCount={unreadCount}
          enableBadge
        />
      ) : null}
      <div className={divider()}>
        {navItems.map((item, i) => {
          return (
            <ClosedItemDropdown
              key={i}
              item={item}
              isTouchDevice={isTouchDevice}
              openDropdownMenuId={openDropdownMenuId}
              onSelect={setOpenDropdownMenuId}
              isOpen={isOpen}
              onPointerLeave={handleDropdownMenuPointerLeave}
              onPointerEnter={(event) => handleDropdownMenuPointerEnter(event, item.id)}
              onKeyDown={(event) => handleDropdownMenuKeyDown(event, item.id)}
            />
          );
        })}
      </div>
      <ClosedSavedItems
        savedItems={savedItems}
        onDelete={deleteSavedItem}
        isOpen={isOpen}
        isTouchDevice={isTouchDevice}
        openDropdownMenuId={openDropdownMenuId}
        onSelect={setOpenDropdownMenuId}
        onPointerLeave={handleDropdownMenuPointerLeave}
        onPointerEnter={(event) => handleDropdownMenuPointerEnter(event, SAVED_ITEMS_MENU_ID)}
        onKeyDown={(event) => handleDropdownMenuKeyDown(event, SAVED_ITEMS_MENU_ID)}
      />
    </nav>
  );
};

const styles = tv({
  slots: {
    base: 'absolute bottom-0 left-2.5 top-0 max-md:hidden',
    divider:
      'after:opacity-1 relative mt-[3px] pt-[25px] after:absolute after:left-1 after:right-1 after:top-3.5 after:border-t after:border-blue-400 after:transition-all after:content-[""]',
  },
  variants: {
    isOpen: {
      true: {
        base: 'pointer-events-none',
        divider: 'after:opacity-0',
      },
    },
  },
});

export { AppNavigationClosed };
